import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { useTheme } from 'styled-components/macro';
import { IconChevron, IconWrapper } from '../../icons';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils';
import chroma from 'chroma-js';

const AccordionButtonStyled = styled.div<{ primary: string }>`
  display: block;
  position: relative;
  width: 100%;
  border: 1px solid ${(p) => p.primary};
  background: ${(p) => p.primary};
  padding: ${SPACINGS[6]};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: ${(p) => p.theme.boxShadows.xs};

  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 2;
  }

  .description-container {
    position: relative;
    z-index: 2;
  }

  .head {
    padding-bottom: ${SPACINGS[4]};
  }

  .description {
    padding-top: ${SPACINGS[4]};
  }

  .image {
    z-index: 1;
    position: absolute;
    bottom: 0;
    right: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;

    &.visible {
      opacity: 1;
    }
  }
`;

const variants = {
  open: { opacity: 1, height: 'auto' },
  closed: { opacity: 0, height: 0 },
};

type AccordionInfoProps = {
  title: string | React.ReactNode;
  head?: React.ReactNode;
  description?: string | React.ReactNode;
  descriptionStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  initialIsOpen?: boolean;
  primaryColor?: string;
  image?: string;
  imageStyle?: React.CSSProperties;
  buttonBackgroundColor?: string;
  textColor: string;
};
function AccordionInfo(props: AccordionInfoProps) {
  const theme = useTheme();
  const defaultPrimaryColor = theme.colors.secondary;
  const defaultTextColor = theme.colors.white;
  const defaultButtonBackgroundColor = theme.colors.primary95w;

  const {
    title,
    head,
    description,
    descriptionStyle,
    initialIsOpen,
    style,
    image,
    imageStyle,
    primaryColor = defaultPrimaryColor,
    buttonBackgroundColor = defaultButtonBackgroundColor,
    textColor = defaultTextColor
  } = props;

  const [isOpen, setIsOpen] = React.useState(initialIsOpen);
  function handleChevronClick(e: React.MouseEvent<HTMLDivElement>) {
    setIsOpen((b) => !b);
    e.stopPropagation();
  }

  return (
    <AccordionButtonStyled style={style} primary={primaryColor}>
      {head && <div className="head">{head}</div>}
      <div className="title">
        <>
          {typeof title === 'string' ? (
            <Text margin="none" style={{color: textColor}}>{title}</Text>
          ) : (
            title
          )}
        </>
        {description && (
          <IconWrapper
            style={{
              minWidth: SPACINGS[8],
              minHeight: SPACINGS[8],
            }}
            backgroundColor={theme.colors.primary95w}
            hoverColor={theme.colors.primary90w}
            type="action"
            onClick={handleChevronClick}
          >
            <IconChevron direction={isOpen ? 'top' : 'bottom'} stroke={theme.colors.primary} />
          </IconWrapper>
        )}
      </div>
      {description && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="description-container"
              initial="closed"
              animate="open"
              exit="closed"
              variants={variants}
            >
              <div className="description" style={descriptionStyle}>
                {typeof description === 'string' ? (
                  <Text type="label" size="sm" margin="none" style={{color: textColor}}>
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {image && (
        <img
          style={imageStyle}
          className={`image ${isOpen && 'visible'}`}
          alt="background"
          src={image}
        />
      )}
    </AccordionButtonStyled>
  );
}

export default AccordionInfo;
